import { createI18n } from 'vue-i18n';

const messages = {
    en: {
        //Translations relating to shared components.
        common: {
            tripType: {
                ROUND_TRIP: 'Round Trip',
                ONE_WAY: 'One Way',
                MULTI_CITY: 'Multi City'
            },
            cabinClass: {
                BASIC_ECONOMY: 'Basic Economy',
                ECONOMY: 'Economy',
                PREMIUM_ECONOMY: 'Premium Economy',
                BUSINESS: 'Business',
                FIRST: 'First Class'
            },
            //This is needed for sending transactions to google analytics.
            fareType: {
                CONSOLIDATOR: 'Consolidator',
                CRUISE: 'Cruise',
                HUMANITARIAN: 'Humanitarian',
                PUBLISHED: 'Published',
                COMMISSIONABLE: 'Commisionable',
                STUDENT: 'Student',
                TOUR: 'Tour'
            },
            bookend: {
                about: 'About',
                trustmarkHeader: 'Your Payment is Secure',
                trustmarkInformation: 'We partner with some of the top names in payment processing to ensure your payment is securely handled.',
                termsConditions: 'Terms & Conditions',
                privacyPolicy: 'Privacy Policy'
            },
            form: {
                country: 'Country',
                province: 'Province',
                state: 'State'
            },
            itinerary: {
                itinerary: 'Itinerary',
                showMoreDetails: 'Show More Details',
                showFewerDetails: 'Show Fewer Details',
                multipleAirlines: 'Multiple Airlines',
                duration: 'Duration',
                operatedBy: 'Operated by {operatingAirlineNames}',
                mapPinPopper: '{time} in {airportCode}',
                flightTime: '{time} Flight Time',
                layoverIn: '{time} layover in {city}',
                layover: '{time} layover',
                changeOfPlane: 'May require change of plane.'
            },
            travelAdvisor: {
                yourTravelAdvisor: 'Your Travel Advisor',
                address: 'Address',
                yourAgent: 'Your Agent' //For Groups - shows Managed Trip agent.
            },
            travelers: {
                travelers: 'Travelers',
                ticketNumber: 'Ticket Number|Ticket Numbers',
                frequentTravelerNumber: 'Frequent Traveler #|Frequent Traveler #s',
                totalPrice: 'Total Price'
            },
            tripSummary: {
                yourTripSummary: 'Your Trip Summary',
                tripReference: 'Trip Reference',
                airline: 'Airline',
                cities: 'Cities',
                travelers: 'Travelers',
                dateReserved: 'Date Reserved',
                travelDate: 'Travel Date'
            },
            loading: 'Loading...'
        },

        //Translations specific to the Find view.
        find: {
            pageTitle: 'Find My Trip',
            confirmationNumberOrRecordLocator: 'Confirmation Number or Record Locator',
            confirmationNumber: 'Confirmation Number',
            travelersLastName: 'Traveler\'s Last Name',
            backToTrip: 'Back to Trip',
            findingYourTrip: 'Finding Your Trip',
            errors: {
                default: 'Whoops! Please correct the errors above.',
                notFound: 'Uh oh! We couldn\'t find a matching trip.',
                recaptcha: 'Uh oh! Something went wrong. Please refresh and try again.'
            }
        },

        //Translations specific to the Payment view.
        payment: {
            pageTitle: 'Secure Payment',
            backToBookingSummary: 'Back to Booking Summary',
            backToMyTrip: 'Back to My Trip',
            completingYourPayment: 'Completing Your Payment',
            paymentDue: 'Payment Due',
            noteFromTravelAdvisor: 'Note From Your Travel Advisor',
            chargeSingleCard: 'Charge the total amount to a single credit card.',
            chargeMultiCard: 'Split the charge by traveler, across multiple credit cards.',
            creditCardTitle: 'Credit Card # {number}',
            remove: 'Remove',
            whichTravelers: 'Which travelers should be paid for on this card?',
            total: 'Total',
            multipleChargesWillBeMade: 'Multiple charges will be made.',
            chargeForAirfare: '{amount} charge for airfare',
            chargeForFees: '{amount} charge for fees',
            creditCardInformation: 'Credit Card Information',
            creditCardNumber: 'Credit Card Number',
            nameOnCard: 'Name on Card',
            expiration: 'Expiration',
            cvv: 'CVV',
            airlineDeterminesSoftDescriptor: 'The airline determines how the airfare charge will appear on your statement.',
            feesSoftDescriptor: 'Fees will appear on your statement as',
            chargeSoftDescriptor: 'Charge will appear on your statement as',
            billingAddress: 'Billing Address',
            streetAddress: 'Street Address',
            city: 'City',
            postalCode: 'Postal Code',
            cardholderContactInformation: 'Cardholder Contact Information',
            emailAddress: 'Email Address',
            phoneNumber: 'Phone Number',
            contactInformationCollectionNote: 'We collect contact information for payment verification purposes only.',
            addAnotherCard: 'Add Another Card',
            paymentTermsStart: 'I have read the',
            creditCardPaymentAgreement: 'credit card payment agreement',
            paymentTermsEnd: 'and agree to the terms detailed within.',
            completePayment: 'Complete Payment',
            //TODO translate to ES
            bankTransferInstructions: {
                bankTransferInstructions: 'Bank Transfer Instructions',
                bankTransferInstructionDetails: 'Please submit an ACH or wire transfer to the bank listed below, and contact your agent directly when it has been completed.',
                bankName: 'Bank Name',
                accountName: 'Account / Beneficiary Name',
                achRoutingNumber: 'ACH Routing Number',
                wireRoutingNumber: 'Wire Routing Number',
                accountNumber: 'Account Number',
                swiftCode: 'Swift Code (USD Only)',
                accountAddress: 'Account Address',
                bankAddress: 'Bank Address',
                phoneNumber: 'Phone Number',
                accountingContact: 'Accounting Contact',
                otherCurrencyWireTransferNote: 'For wire transfer in a currency other than USD, please contact us.'
            },
            reason: {
                PAYMENT_IN_FULL: 'Provide payment information to pay for this trip and receive ticket numbers.', //Special - this is not actually a reason code.

                CHANGE_FEE: 'Payment is required in order to complete a change to this trip.',
                CHARGEBACK: 'There was a chargeback on this booking. Please provide replacement payment information below.',
                COMMISSION_RECALL: 'Your commission has been recalled due to a change to this trip. Provide repayment information below.',
                DO_NOT_HONOR: 'There was an issue processing your previous payment, and new payment information is required.',
                FINAL_PAYMENT: 'Provide payment information for your final payment below.',
                GROUP_DEPOSIT: 'Provide payment information for your group deposit below.',
                EMERGENCY_SUPPORT_INCIDENTALS: 'This payment information will be charged if additional payment is required in order process a change to this trip.',
                OTHER: 'Payment is required in order to complete a change to this trip.'
            },
            errors: {
                invalid: 'Whoops! Please correct the errors above.',
                allTravelersAssigned: 'All travelers have been assigned to other credit cards.',
                travelersNotAssigned: 'One or more travelers have not been assigned to a credit card.',//TODO
                creditCardNotAssigned: 'One or more credit cards have not had any travelers assigned.',//TODO
                getTripAndLink: {
                    default: 'Uh oh! Something went wrong while finding your trip.',
                    linkCompleted: 'You have already provided payment using this secure payment link. Thank you!',
                    linkExpired: 'Uh oh! This secure payment link is expired. Contact your travel advisor for assistance.'
                },
                completePayment: {
                    default: 'Uh oh! Something went wrong while processing your payment.',
                    paymentIssue: 'Uh oh! We found an issue with your payment.',
                    reviewCardholderBillingInformation: 'Please review the cardholder and billing information for accuracy and try again.',
                    missingTravelerAssignments: 'One or more travelers have not been assigned to a credit card.',
                    missingCardAssignments: 'One or more credit cards have not had any travelers assigned.'
                }
            }
        },

        //Translations specific to the Trip view.
        trip: {
            pageTitle: 'My Trip',
            payForThisTrip: 'Pay for This Trip',

            //notices and warnings
            securePaymentReceived:'Your payment information is secure.',
            travelAgentContact: 'If there are issues when your payment is processed, your travel agent will contact you.',
            thankYouForUsing: 'Thank you for using Pay For My Trip.',
            contactTravelAdvisor: 'Please contact your travel advisor for assistance.',
            payBy: 'Pay by {time} CT',
            originDestination: {
                type: {
                    destination: 'Destination {number}',
                    inbound: 'Returning',
                    outbound: 'Departing'
                },
                stops: 'Nonstop|One Stop|{n} Stops'
            },
            errors: {
                unableToProcess: 'Sorry, we were unable to process your payment.',
                needsPaymentNoLink: 'This trip needs payment but there isn\'t an active secure payment link.',
                getTrip: {
                    default: 'Uh oh! Something went wrong while finding your trip.',
                }
            }
        },

        //Translations related to validation.
        validate: {
            fields: {
                confirmationNumber: 'confirmation number',
                lastName: 'last name',
                creditCardNumber: 'credit card number',
                nameOnCard: 'name on card',
                expiration: 'expiration',
                cardSecurityCode: 'card security code',
                streetAddress: 'street address',
                city: 'city',
                state: 'state',
                province: 'province',
                postalCode: 'postal code',
                country: 'country',
                emailAddress: 'email address',
                phoneNumber: 'phone number',
                creditCardPaymentAgreement: 'credit card payment agreement'
            }
        }
    },
    es: {
        //Translations relating to shared components.
        common: {
            tripType: {
                ROUND_TRIP: 'Ida y Regreso',
                ONE_WAY: 'Una Via',
                MULTI_CITY: 'Multi Ciudad'
            },
            cabinClass: {
                BASIC_ECONOMY: 'Económica Básica',
                ECONOMY: 'Económica',
                PREMIUM_ECONOMY: 'Económica Superior',
                BUSINESS: 'Negocios',
                FIRST: 'Primera Clase'
            },
            fareType: {
                CONSOLIDATOR: 'Consolidador',
                CRUISE: 'Crucero',
                HUMANITARIAN: 'Humanitaria',
                PUBLISHED: 'Publicada',
                COMMISSIONABLE: 'Comisionable',
                STUDENT: 'Estudiante',
                TOUR: 'Tour'
            },
            bookend: {
                about: 'Acerca De',
                trustmarkHeader: 'Su Pago es Seguro',
                trustmarkInformation: 'Nos asociamos con algunos de los principales Nombres en el procesamiento de pagos para garantizar que su pago se maneje de manera segura.',
                termsConditions: 'Términos y Condiciones',
                privacyPolicy: 'Política de Privacidad',
            },
            form: {
                country: 'País',
                province: 'Provincia',
                state: 'Estado',
            },
            itinerary: {
                itinerary: 'Itinerario',
                showMoreDetails: 'Mostrar más Detalles',
                showFewerDetails: 'Mostrar menos Detalles',
                multipleAirlines: 'Múltiples Aerolíneas',
                duration: 'Duración',
                operatedBy: 'Operado por {operatingAirlineNames}',
                mapPinPopper: '{time} en {airportCode}',
                flightTime: 'Tiempo de Vuelo {time}',
                layoverIn: 'Escala de {time} en {city}',
                layover: 'Escala de {time}',
                changeOfPlane: 'Puede requerir cambio de avión.',
            },
            travelAdvisor: {
                yourTravelAdvisor: 'Su Asesor de Viajes',
                address: 'Dirección',
                yourAgent: 'Su Agente'
            },
            travelers: {
                travelers: 'Viajeros',
                ticketNumber: 'Número de Boleto|Números de Boletos',
                frequentTravelerNumber: 'Pasajero Frecuente #|Pasajeros Frecuentes #s',
                totalPrice: 'Precio Total',
            },
            tripSummary: {
                yourTripSummary: 'Resumen de su Viaje',
                tripReference: 'Referencia de Viaje',
                airline: 'Aerolínea',
                cities: 'Ciudades',
                travelers: 'Viajeros',
                dateReserved: 'Fecha de Reserva',
                travelDate: 'Fecha de Viaje',
            },
            loading: 'Cargando...'
        },

        //Translations specific to the Find view.
        find: {
            pageTitle: 'Buscar mi Viaje',
            confirmationNumberOrRecordLocator: 'Número de confirmación o Record Localizador',
            confirmationNumber: 'Número de Confirmación',
            travelersLastName: 'Apellido del Viajero',
            backToTrip: 'Volver al Viaje',
            findingYourTrip: 'Buscando su Viaje',
            errors: {
                default: 'Por Favor ! Corrija los errores anteriores.',
                notFound: 'Oh oh! No pudimos encontrar un viaje que coincida.',
                recaptcha: 'Oh oh! Algo salió mal. Por favor, actualice y pruebe de nuevo.',
            }
        },

        //Translations specific to the Payment view.
        payment: {
            pageTitle: 'Pago Seguro',
            backToBookingSummary: 'Volver al Resumen de la Reserva',
            backToMyTrip: 'Volver a Mi Viaje',
            completingYourPayment: 'Completando Su Pago',
            paymentDue: 'Fecha de Pago',
            noteFromTravelAdvisor: 'Nota de su Asesor de Viajes',
            chargeSingleCard: 'Cargue el monto total a una sola tarjeta de crédito.',
            chargeMultiCard: 'Divida el cargo de cada uno, a través de múltiples tarjetas de crédito.',
            creditCardTitle: 'Tarjeta de Crédito # {number}',
            remove: 'Eliminar',
            whichTravelers: '¿Qué viajeros se deben pagar con esta tarjeta?',
            total: 'Total',
            multipleChargesWillBeMade: 'Se realizarán cargos múltiples.',
            chargeForAirfare: 'Cargo de {amount} por Pasaje',
            chargeForFees: 'cargo de {amount} por Honorarios',
            creditCardInformation: 'Información de Tarjeta de Crédito',
            creditCardNumber: 'Número de Tarjeta de Crédito',
            nameOnCard: 'Nombre en la Tarjeta',
            expiration: 'Vencimiento',
            cvv: 'Cod de Seguridad',
            airlineDeterminesSoftDescriptor: 'La aerolínea determina cómo aparece el cargo aéreo en su estado de cuenta.',
            feesSoftDescriptor: 'La tarifa aparecerá en su estado de cuenta como',
            chargeSoftDescriptor: 'El cargo aparecerá en su estado de cuenta como',
            billingAddress: 'Dirección de Facturación',
            streetAddress: 'Calle',
            city: 'Ciudad ',
            postalCode: 'Codigo Postal',
            cardholderContactInformation: 'Info. de Contacto del Titular de la Tarjeta',
            emailAddress: 'Correo electrónico',
            phoneNumber: 'Número de Teléfono',
            contactInformationCollectionNote: 'Recopilamos esta información únicamente con fines de verificación de pagos.',
            addAnotherCard: 'Agregar otra Tarjeta',
            paymentTermsStart: 'He leído',
            creditCardPaymentAgreement: 'el acuerdo de pago con tarjeta de crédito',
            paymentTermsEnd: ' y acepto los términos que se detallan en él.',
            completePayment: 'Pago Completo',
            reason: {
                PAYMENT_IN_FULL: 'Proporcione información para el pago de este viaje y recibir números de boleto.',
                CHANGE_FEE: 'Se requiere el pago para completar un cambio en este viaje.',
                CHARGEBACK: 'Hubo una devolución en este pago. Proporcione la nueva infomación de pago a continuación.',
                COMMISSION_RECALL: 'Su comisión ha sido revocada debido a un cambio en este viaje. Proporcione la nueva información de pago a continuación.',
                DO_NOT_HONOR: 'Hubo un problema al procesar su pago anterior y se requiere nueva información de pago.',
                FINAL_PAYMENT: 'Proporcione la información de pago final a continuación.',
                GROUP_DEPOSIT: 'Proporcione la información de pago para el depósito de su grupo a continuación.',
                OTHER: 'Se requiere el pago para completar un cambio en este viaje.',
            },
            errors: {
                invalid: 'Por favor, corregir los errores anteriores.',
                allTravelersAssigned: 'Todos los viajeros han sido asignados a otras tarjetas de crédito.',
                getTripAndLink: {
                    default: '¡Oh oh! Algo salió mal buscando tu viaje.',
                    linkCompleted: 'Usted ha realizado el pago mediante este enlace de pago seguro. ¡Gracias!',
                    linkExpired: 'Este enlace de pago ha caducado. Contacte a su asesor de viajes para obtener ayuda.',
                },
                completePayment: {
                    default: '¡Oh oh! Algo salió mal al procesar su pago.',
                    paymentIssue: '¡Oh oh! Encontramos un problema con su pago.',
                    reviewCardholderBillingInformation: 'Por favor revise todos los datos para ver si son correctos e inténtelo de nuevo.',
                    missingTravelerAssignments: 'Uno o más viajeros no han sido asignados a una tarjeta de crédito.',
                    missingCardAssignments: 'Una o más tarjetas de crédito no han sido asignadas a ningun viajero.',
                }
            }
        },

        //Translations specific to the Trip view.
        trip: {
            pageTitle: 'Mi Viaje',
            payForThisTrip: 'Pagar Este Viaje',
            securePaymentReceived: 'Su Pago Seguro ha sido recibido.',
            travelAgentContact: 'Si hay algún problema con el procesamiento de su pago, su agente de viajes se comunicará con usted.',
            thankYouForUsing: 'Gracias por usar Pay For My Trip',
            contactTravelAdvisor: 'Póngase en contacto con su asesor de viajes para obtener ayuda.',
            payBy: 'Pague antes del {time} CT',
            originDestination: {
                stops: 'Sin escalas|Una Escala|{n} Escalas',
                type: {
                    destination: 'Destino {number}',
                    inbound: 'Regresando',
                    outbound: 'Saliendo',
                }
            },
            errors: {
                unableToProcess: 'Lo sentimos, no pudimos procesar su pago.',
                needsPaymentNoLink: 'Este viaje necesita un pago pero no hay un enlace de Pago Seguro activo.',
                getTrip: {
                    default: '¡Oh oh! Algo salió mal al encontrar tu viaje.'
                },
            }
        },

        validate: {
            fields: {
                confirmationNumber: 'número de confirmación',
                lastName: 'apellidos',
                creditCardNumber: 'número de tarjeta de crédito',
                nameOnCard: 'nombre en la tarjeta',
                expiration: 'vencimiento',
                cardSecurityCode: 'código de seguridad',
                streetAddress: 'dirección',
                city: 'ciudad',
                state: 'estado',
                province: 'provincia',
                postalCode: 'codigo postal',
                country: 'país',
                emailAddress: 'correo electrónico',
                phoneNumber: 'número de teléfono',
                creditCardPaymentAgreement: 'acuerdo de pago con tarjeta de crédito'
            }
        }
    }
}

// //Uncomment this to flatten & diff translations.
// //Use this to help determine which translation keys we still need spanish translations for.
//
// //Flattens a nested object structure using dot notation.
// function dotNotate(obj,target,prefix) {
//     target = target || {},
//         prefix = prefix || "";
//
//     Object.keys(obj).forEach(function(key) {
//         if ( typeof(obj[key]) === "object" && obj[key] !== null ) {
//             dotNotate(obj[key],target,prefix + key + ".");
//         } else {
//             return target[prefix + key] = obj[key];
//         }
//     });
//
//     return target;
// }
//
// let flatSource = dotNotate(messages.en);
// let flatTarget = dotNotate(messages.es);
// let diff = {};
//
// for (const [key, value] of Object.entries(flatSource)) {
//     if (flatTarget[key] === undefined) diff[key] = value;
// }
//
// console.log(diff);

const i18n = createI18n({
    //TODO use locale in state.
    locale: 'en', // set locale
    fallbackLocale: process.env.VUE_APP_FALLBACK_LOCALE, // set fallback locale
    messages, // set locale messages
});

export default i18n;